/**
 * Hand edits that survive regeneration. Browser-safe, pure.
 *
 * An edit is stored as `{ field, before, after }`. After every regeneration
 * it is re-applied to the FIRST place `before` still appears in the new text
 * (never every place: the user edited one passage, and recurring name fixes
 * belong in the names-and-terms list); when it no longer appears the edit is
 * reported as stale, so the user sees a notice that it no longer applies
 * instead of losing it silently.
 */

import type { ManualEdit, TitleOption } from "./types";

export interface ManualEditResult {
	description: string;
	titles: string[];
	applied: string[];
	stale: string[];
}

/** Apply the edits in order to the rendered description and title texts. */
export function applyManualEdits(
	input: { description: string; titleOptions: readonly TitleOption[] },
	edits: readonly ManualEdit[] | null | undefined,
): ManualEditResult {
	let description = input.description;
	const titles = input.titleOptions.map((t) => t.text);
	const applied: string[] = [];
	const stale: string[] = [];
	for (const edit of edits ?? []) {
		if (!edit.before || edit.before === edit.after) {
			stale.push(edit.id);
			continue;
		}
		if (edit.field === "description") {
			if (description.includes(edit.before)) {
				description = replaceFirst(
					description,
					edit.before,
					edit.after,
				);
				applied.push(edit.id);
			} else {
				stale.push(edit.id);
			}
			continue;
		}
		const index = edit.titleIndex ?? 0;
		const current = titles[index];
		if (current?.includes(edit.before)) {
			titles[index] = replaceFirst(current, edit.before, edit.after);
			applied.push(edit.id);
		} else {
			stale.push(edit.id);
		}
	}
	return { description, titles, applied, stale };
}

/** Replace only the first occurrence (no regex, so `$` patterns stay literal). */
function replaceFirst(text: string, before: string, after: string): string {
	const at = text.indexOf(before);
	return at === -1
		? text
		: text.slice(0, at) + after + text.slice(at + before.length);
}

/**
 * The smallest `{ before, after }` pair that turns `original` into `edited`,
 * widened to whole words so the edit still finds its place after a
 * regeneration rewords the text around it, and then by whole words until
 * `before` appears only once in `original`, so the edit points at the one
 * passage the user changed (re-application then targets its first match).
 * Null when nothing changed.
 */
export function computeManualEdit(
	original: string,
	edited: string,
): { before: string; after: string } | null {
	if (original === edited) return null;
	let start = 0;
	const maxStart = Math.min(original.length, edited.length);
	while (start < maxStart && original[start] === edited[start]) start++;
	let endO = original.length;
	let endE = edited.length;
	while (
		endO > start &&
		endE > start &&
		original[endO - 1] === edited[endE - 1]
	) {
		endO--;
		endE--;
	}
	// Widen to word boundaries on both sides.
	const isWordChar = (ch: string | undefined) => !!ch && !/\s/.test(ch);
	while (start > 0 && isWordChar(original[start - 1])) start--;
	while (endO < original.length && isWordChar(original[endO])) {
		endO++;
		endE++;
	}
	if (start === endO) {
		// Pure insertion: anchor on the word before it.
		let anchor = start;
		while (anchor > 0 && /\s/.test(original[anchor - 1] ?? "")) anchor--;
		while (anchor > 0 && isWordChar(original[anchor - 1])) anchor--;
		if (anchor === start) return { before: original, after: edited };
		start = anchor;
	}
	// Widen by a word at a time (left, then right) until `before` is unique.
	const occurrences = (text: string) => original.split(text).length - 1;
	while (
		occurrences(original.slice(start, endO)) > 1 &&
		(start > 0 || endO < original.length)
	) {
		if (start > 0) {
			while (start > 0 && !isWordChar(original[start - 1])) start--;
			while (start > 0 && isWordChar(original[start - 1])) start--;
		}
		if (occurrences(original.slice(start, endO)) <= 1) break;
		if (endO < original.length) {
			const from = endO;
			while (endO < original.length && !isWordChar(original[endO]))
				endO++;
			while (endO < original.length && isWordChar(original[endO])) endO++;
			endE += endO - from;
		}
	}
	return {
		before: original.slice(start, endO),
		after: edited.slice(start, endE),
	};
}
